/**
 * @fileoverview System-web API client for code-set-cache admin actions.
 * @module system-web/violation-code-sets
 *
 * Responsibilities:
 * - Wrap POST /api/code-set-cache/:cacheKey/{rebuild,archive,verify} endpoints.
 *
 * Dependencies:
 * - `apiClient.apiFetch` for authenticated fetch.
 *
 * Architecture:
 * - These actions are INHERENTLY ONLINE admin operations that mutate server
 *   state: they are exempt from the local-first Dexie rule. Reads for the
 *   catalog grid still come from Dexie (see systemViolationCodeSetLibraryService).
 *
 * Notes:
 * - Optimistic UI updates are deliberately avoided; rely on the subsequent
 *   background job + change-stream sync to reflect the new manifest state.
 */

import { apiFetch } from '../apiClient';

/**
 * @contract CodeSetCacheVerifyReport
 *
 * Purpose:
 * - Per-chapter integrity report returned by Verify Integrity.
 *
 * Fields:
 * - overallStatus: Aggregate outcome.
 * - chapterCount / okCount / mismatchCount / missingCount: Summary counts.
 * - chapters: Per-chapter detail rows.
 */
export interface CodeSetCacheVerifyReport {
  cacheKey: string;
  verifiedAt: string;
  overallStatus: 'ok' | 'corrupted';
  chapterCount: number;
  okCount: number;
  mismatchCount: number;
  missingCount: number;
  chapters: Array<{
    chapterId: string;
    blobPath: string;
    status: 'ok' | 'mismatch' | 'missing';
    expectedSha256: string;
    actualSha256: string | null;
    bytes: number;
    error?: string;
  }>;
}

/**
 * @process RebuildCodeSetCache
 *
 * Steps:
 * 1. POST /api/code-set-cache/:cacheKey/rebuild with resume or restart mode.
 * 2. Server enqueues a codeSetDownload job against the first matching tenant.
 *
 * Inputs:
 * - cacheKey: Vendor+book+printing compound key.
 *
 * Outputs:
 * - Newly-created background job id and selected recovery mode.
 *
 * Failure Handling:
 * - 404 when manifest missing, 409 when no tenant references the cacheKey.
 */
export async function rebuildCodeSetCache(
  cacheKey: string,
  mode: 'resume' | 'restart' = 'resume',
): Promise<{ jobId: string; status: string; mode: 'resume' | 'restart' }> {
  return apiFetch(`/api/code-set-cache/${encodeURIComponent(cacheKey)}/rebuild`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

/**
 * @process ArchiveCodeSetCache
 *
 * Steps:
 * 1. POST /api/code-set-cache/:cacheKey/archive.
 * 2. Server deletes blobs + removes manifest row + fanout-marks tenants only when blob cleanup fully succeeds.
 *
 * Failure Handling:
 * - 409 when blob cleanup fails; the server keeps the manifest row for retry.
 */
export async function archiveCodeSetCache(cacheKey: string): Promise<{ archived: boolean; blobsDeleted: number; blobDeleteError?: string }> {
  return apiFetch(`/api/code-set-cache/${encodeURIComponent(cacheKey)}/archive`, { method: 'POST' });
}

/**
 * @process VerifyCodeSetCache
 *
 * Steps:
 * 1. POST /api/code-set-cache/:cacheKey/verify.
 * 2. Server streams each chapter blob, recomputes SHA-256 in parallel.
 * 3. Server updates `lastVerifiedAt` + `verifyStatus` on the manifest row.
 */
export async function verifyCodeSetCache(cacheKey: string): Promise<CodeSetCacheVerifyReport> {
  return apiFetch(`/api/code-set-cache/${encodeURIComponent(cacheKey)}/verify`, { method: 'POST' });
}

/**
 * @process FetchCodeSetCacheDetail
 *
 * Steps:
 * 1. GET /api/code-set-cache/:cacheKey.
 * 2. Returns manifest + tenantFanout for the admin slide-out panel.
 */
export async function fetchCodeSetCacheDetail(cacheKey: string): Promise<{
  manifest: Record<string, unknown>;
  tenantFanout: Array<{
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    lastHydratedAt: string | null;
    downloadStatus: string | null;
  }>;
}> {
  return apiFetch(`/api/code-set-cache/${encodeURIComponent(cacheKey)}`);
}
